import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import HeaderMain from "../dasboard-component/Components/HeaderMain";
import { useAuth } from "../context/AuthContext";
import { useInstituteAuth } from "../context/InstituteAuthContext";
import { toast } from "react-toastify";

/**
 * JSON shape we persist per swimming event
 * {
 *   eventName: string,
 *   eventType: "individual" | "relay",
 *   stroke: string,
 *   distance: number,
 *   poolLength: number,
 *   customDistance: boolean,
 *   customPoolLength: boolean,
 *   participants: [
 *     { id, userId, name, lane, finalTime, splits: [{ distance, time }], reaction, strokeCount, place, isCustom }
 *   ],
 *   relayTeams: [
 *     {
 *       id, teamName, lane, totalTime, place,
 *       swimmers: [{ id, userId, name, leg, split, strokeCount, isCustom }],
 *       splits: [] // optional relay splits
 *     }
 *   ],
 *   records: { meetRecord, facilityRecord, personalBests: [] }
 * }
 */

const brandBlue = "#3498db";
const brandBlueDark = "#2980b9";

export const AddSwimming = () => {
  const location = useLocation();
  const data = location.state || {};
  const navigate = useNavigate();

  const [swimmingEvents, setSwimmingEvents] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const { user } = useAuth();
  const { instituteUser } = useInstituteAuth();

  // Check if this is institute context
  const isInstitute = data.isInstitute || false;
  const instituteId = data.instituteId || instituteUser?.id;

  // Fetch available users for swimmer selection
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/user-add-game/api/selection/users`,
          { credentials: "include" }
        );
        if (response.ok) {
          const users = await response.json();

          // Get current user ID
          const currentUserId = user?.uid;

          // Prioritize invited users and show their status
          const invitedUsers = (data.invitedUsers || []).map((user) => ({
            ...user,
            isInvited: true,
            invitationStatus: user.status,
          }));

          // Find current user and add them if not already in invited users
          const currentUser = users.find((user) => user.id == currentUserId);
          const currentUserIsInvited = invitedUsers.some(
            (invited) => invited.user_id == currentUserId
          );

          let finalUserList = [...invitedUsers];

          // Add current user if not already invited
          if (currentUser && !currentUserIsInvited) {
            finalUserList.push({
              ...currentUser,
              isCurrent: true,
            });
          }

          // Filter out invited users AND current user from the general list to avoid duplicates
          const otherUsers = users.filter(
            (user) =>
              !invitedUsers.some((invited) => invited.user_id === user.id) &&
              user.id != currentUserId
          );

          // Add other users
          finalUserList = [...finalUserList, ...otherUsers];

          setAvailableUsers(finalUserList);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };
    fetchUsers();
  }, [data.invitedUsers]);

  const validateEvents = () => {
    const errors = [];

    swimmingEvents.forEach((event, index) => {
      // Check event name
      if (!event.eventName) {
        errors.push(`Event ${index + 1}: Event name is required`);
      }

      // Check stroke and distance
      if (!event.stroke || !event.distance) {
        errors.push(`Event ${index + 1}: Stroke and distance are required`);
      }

      // Check participants
      if (event.eventType === "individual") {
        if (event.participants.length === 0) {
          errors.push(
            `Event ${
              index + 1
            }: At least one swimmer is required for individual events`
          );
        }

        // Validate times
        event.participants.forEach((participant, pIndex) => {
          if (!participant.finalTime || !isValidTime(participant.finalTime)) {
            errors.push(
              `Event ${index + 1}, Swimmer ${
                pIndex + 1
              }: Valid final time is required (MM:SS.mm format)`
            );
          } else {
            // Check if time is reasonable (not too fast or too slow)
            const timeInSeconds = timeToSeconds(participant.finalTime);
            const minTime = event.distance * 0.5; // Very fast swimmer: 0.5 seconds per meter
            const maxTime = event.distance * 10; // Very slow swimmer: 10 seconds per meter

            if (timeInSeconds < minTime) {
              errors.push(
                `Event ${index + 1}, Swimmer ${pIndex + 1}: Time ${
                  participant.finalTime
                } seems too fast for ${event.distance}m`
              );
            }
            if (timeInSeconds > maxTime) {
              errors.push(
                `Event ${index + 1}, Swimmer ${pIndex + 1}: Time ${
                  participant.finalTime
                } seems too slow for ${event.distance}m`
              );
            }
          }

          // Split times are optional but could be recommended for longer distances
          // Note: Split times are not required but can be useful for performance analysis

          // Validate swimmer name
          if (!participant.name || participant.name.trim() === "") {
            errors.push(
              `Event ${index + 1}, Swimmer ${
                pIndex + 1
              }: Swimmer name is required`
            );
          }
        });
      } else if (event.eventType === "relay") {
        if (event.relayTeams.length === 0) {
          errors.push(
            `Event ${index + 1}: At least one relay team is required`
          );
        }

        event.relayTeams.forEach((team, tIndex) => {
          if (team.swimmers.length !== 4) {
            errors.push(
              `Event ${index + 1}, Team ${
                tIndex + 1
              }: Relay teams must have exactly 4 swimmers`
            );
          }

          if (!team.totalTime || !isValidTime(team.totalTime)) {
            errors.push(
              `Event ${index + 1}, Team ${
                tIndex + 1
              }: Valid total time is required`
            );
          }

          // Individual splits are optional for relay teams
          // Note: Split times can be added for detailed performance analysis but are not required
        });
      }
    });

    return errors;
  };

  const isValidTime = (time) => {
    // Validate MM:SS.mm format
    const timePattern = /^([0-5]?[0-9]):([0-5][0-9])\.([0-9]{2})$/;
    return timePattern.test(time);
  };

  const timeToSeconds = (timeStr) => {
    if (!timeStr) return 0;
    const [minutes, seconds] = timeStr.split(":");
    return parseInt(minutes) * 60 + parseFloat(seconds);
  };

  const handleSaveAll = async () => {
    const validationErrors = validateEvents();
    if (validationErrors.length > 0) {
      toast.error(
        "Please fix the following errors:\n\n" + validationErrors.join("\n")
      );
      return;
    }

    try {
      const arenaBookingId =
        data.arenaBooking?.id === "0" ? null : data.arenaBooking?.id;

      const bodyData = {
        arenaBookingId,
        sport: data.sport,
        games: swimmingEvents,
      };

      // Add either userId or instituteId based on context
      if (isInstitute) {
        bodyData.instituteId = instituteId;
      } else {
        bodyData.userId = user?.uid;
      }

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/user-add-game/api/selection/save-match`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(bodyData),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to save events");
      }

      const responseData = await response.json();
      toast.success("Saved all swimming events!");

      // Navigate based on context
      if (isInstitute) {
        navigate("/institute-owner");
      } else {
        navigate("/Manage");
      }
    } catch (error) {
      console.error("Error saving events:", error);
      toast.error("Error saving events");
    }
  };

  const handleAddSwimmingEvent = () => {
    const newEvent = {
      eventName: "",
      eventType: "individual", // individual or relay
      stroke: "freestyle", // freestyle, backstroke, breaststroke, butterfly, individual-medley
      distance: 50, // meters
      poolLength: 25, // 25m or 50m pool
      customDistance: false, // whether user is using custom distance
      customPoolLength: false, // whether user is using custom pool length
      participants: [], // for individual events
      relayTeams: [], // for relay events
      records: {
        meetRecord: null,
        facilityRecord: null,
        personalBests: [],
      },
    };
    setSwimmingEvents((prev) => [...prev, newEvent]);
  };

  const handleRemoveEvent = (index) => {
    setSwimmingEvents((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpdateEvent = (index, updatedEvent) => {
    setSwimmingEvents((prevEvents) =>
      prevEvents.map((e, i) => (i === index ? updatedEvent : e))
    );
  };

  // Tailwind class helpers for consistent styling
  const brandBlue = "#3498db";
  const brandBlueDark = "#2980b9";

  const classes = {
    container:
      "max-w-6xl w-full mx-auto bg-white rounded-2xl shadow-lg font-sans mt-6 px-4 sm:px-6 lg:px-8 py-6 lg:py-8",
    header: "border-b-2 pb-4 mb-6",
    headerTitle: "text-3xl font-semibold text-gray-800 mb-2",
    headerSubtitle: "text-lg text-gray-600",
    addButton:
      "bg-[#3498db] hover:bg-[#2980b9] text-white font-semibold py-3 px-5 rounded-lg shadow-md transition-all duration-200 mb-5",
    saveButton:
      "w-full max-w-[260px] mx-auto block bg-emerald-500 text-white border-2 border-emerald-500 font-semibold py-3 px-4 rounded-lg shadow-md transition-all duration-200 mt-4 hover:bg-white hover:text-emerald-600 hover:-translate-y-0.5",
    noMatchesMessage:
      "text-center p-10 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300 text-gray-500 text-lg",
  };

  return (
    <>
      <HeaderMain pageType="AddGame" />

      <div className={classes.container}>
        <div className={classes.header} style={{ borderColor: brandBlue }}>
          <h2 className={classes.headerTitle}>
            {data.sport} Events at {data.arenaBooking?.name}
          </h2>
          <p className={classes.headerSubtitle}>
            {data.arenaBooking?.date && data.arenaBooking?.time ? (
              <>
                Booking: {data.arenaBooking.date} at {data.arenaBooking.time}
              </>
            ) : (
              "Add detailed swimming event information"
            )}
          </p>
        </div>

        <button
          onClick={handleAddSwimmingEvent}
          className={classes.addButton}
        >
          Add New Swimming Event
        </button>

        {swimmingEvents.length === 0 && (
          <div className={classes.noMatchesMessage}>
            No events added yet. Click the button above to add your first
            swimming event.
          </div>
        )}

        {swimmingEvents.map((event, index) => (
          <SwimmingEventForm
            key={index}
            event={event}
            eventNumber={index + 1}
            availableUsers={availableUsers}
            onUpdate={(updatedEvent) => handleUpdateEvent(index, updatedEvent)}
            onRemove={() => handleRemoveEvent(index)}
          />
        ))}

        {swimmingEvents.length > 0 && (
          <button onClick={handleSaveAll} className={classes.saveButton}>
            Save All Events
          </button>
        )}
      </div>
    </>
  );
};

const SwimmingEventForm = ({
  event,
  eventNumber,
  availableUsers,
  onUpdate,
  onRemove,
}) => {
  const [localEvent, setLocalEvent] = useState(event);
  const [activeTab, setActiveTab] = useState("basic"); // basic, swimmers, results

  const formClasses = {
    matchCard:
      "border border-gray-200 rounded-xl p-5 mb-6 bg-gray-50 shadow-sm",
    matchHeader:
      "flex justify-between items-start md:items-center flex-col md:flex-row gap-3 border-b border-gray-200 pb-4 mb-5",
    matchTitle: "text-xl font-semibold text-gray-800",
    removeButton:
      "bg-red-400 hover:bg-red-500 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200",
    tabContainer: "flex mb-5 border-b border-gray-200 flex-wrap",
    tab: "py-2 px-4 text-sm sm:text-base font-medium border-b-2 border-transparent bg-transparent transition-colors",
    activeTab: "",
    formGroup: "space-y-4",
    label: "font-medium text-gray-700",
    input:
      "w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
    select:
      "px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
    addButton:
      "bg-[#3498db] hover:bg-[#2980b9] text-white font-medium px-3 py-2 rounded-lg transition-colors duration-200",
    teamSection: "bg-white p-5 rounded-lg shadow-sm mb-5",
    playerCard:
      "bg-gray-50 p-3 rounded-lg mb-3 flex justify-between items-center",
    deleteButton: "bg-red-500 text-white px-2 py-1 rounded text-xs",
    guidanceBox:
      "bg-blue-50 border border-blue-200 rounded-lg p-5 shadow-sm mb-5",
    guidanceTitle:
      "text-lg font-semibold text-blue-800 mb-3 flex items-center gap-2",
    guidanceContent: "text-blue-700 space-y-2 text-sm leading-relaxed",
    tableWrapper: "w-full overflow-x-auto",
    table:
      "w-full text-sm border-collapse mt-4 rounded-lg overflow-hidden shadow-sm",
    tableHeader:
      "bg-gray-100 text-gray-800 font-semibold p-3 text-left border-b border-gray-200",
    tableCell: "p-3 border-b border-gray-200 bg-white",
  };

  const updateParent = (updated) => {
    setLocalEvent(updated);
    onUpdate(updated);
  };

  const tabBaseStyle = {
    padding: "10px 16px",
    fontWeight: 600,
    fontSize: "16px",
    color: "#718096",
    border: "none",
    borderBottom: "2px solid transparent",
    background: "transparent",
    cursor: "pointer",
    transition: "all 0.2s ease",
  };

  const tabActiveStyle = {
    color: brandBlue,
    borderBottomColor: brandBlue,
  };

  const addParticipant = (userInfo, customName = "", lane = 1) => {
    const updated = { ...localEvent };
    const participant = {
      id: userInfo?.id || `custom_${Date.now()}`,
      userId: userInfo?.id || null,
      name: userInfo?.full_name || customName || "Guest Swimmer",
      lane: lane,
      finalTime: "",
      splits: [], // {distance: 50, time: "00:28.45"}
      reaction: "", // reaction time (start reaction in seconds)
      strokeCount: 0, // number of strokes taken
      place: 0,
      isCustom: !userInfo,
    };

    updated.participants.push(participant);
    updateParent(updated);
  };

  const removeParticipant = (index) => {
    const updated = { ...localEvent };
    updated.participants.splice(index, 1);
    updateParent(updated);
  };

  const updateParticipant = (index, field, value) => {
    const updated = { ...localEvent };
    updated.participants[index][field] = value;

    // Auto-calculate places based on times
    if (field === "finalTime") {
      calculatePlaces(updated);
    }

    updateParent(updated);
  };

  const addRelayTeam = (teamName = "Team") => {
    const updated = { ...localEvent };
    const relayTeam = {
      id: Date.now(),
      teamName: teamName,
      swimmers: [], // 4 swimmers for relay
      splits: [], // individual leg times
      totalTime: "",
      lane: updated.relayTeams.length + 1,
      place: 0,
    };

    updated.relayTeams.push(relayTeam);
    updateParent(updated);
  };

  const removeRelayTeam = (index) => {
    const updated = { ...localEvent };
    updated.relayTeams.splice(index, 1);
    updateParent(updated);
  };

  const addSwimmerToRelay = (teamIndex, userInfo, customName = "") => {
    const updated = { ...localEvent };
    const swimmer = {
      id: userInfo?.id || `custom_${Date.now()}`,
      userId: userInfo?.id || null,
      name: userInfo?.full_name || customName || "Guest Swimmer",
      leg: updated.relayTeams[teamIndex].swimmers.length + 1,
      split: "", // individual leg time
      strokeCount: 0, // number of strokes taken for this leg
      isCustom: !userInfo,
    };

    if (updated.relayTeams[teamIndex].swimmers.length < 4) {
      updated.relayTeams[teamIndex].swimmers.push(swimmer);
      updateParent(updated);
    }
  };

  const calculatePlaces = (updated) => {
    // Sort participants by time and assign places
    const sortedParticipants = [...updated.participants]
      .filter((p) => p.finalTime)
      .sort((a, b) => timeToSeconds(a.finalTime) - timeToSeconds(b.finalTime));

    sortedParticipants.forEach((participant, index) => {
      const originalIndex = updated.participants.findIndex(
        (p) => p.id === participant.id
      );
      updated.participants[originalIndex].place = index + 1;
    });

    // Same for relay teams
    const sortedTeams = [...updated.relayTeams]
      .filter((t) => t.totalTime)
      .sort((a, b) => timeToSeconds(a.totalTime) - timeToSeconds(b.totalTime));

    sortedTeams.forEach((team, index) => {
      const originalIndex = updated.relayTeams.findIndex(
        (t) => t.id === team.id
      );
      updated.relayTeams[originalIndex].place = index + 1;
    });
  };

  const timeToSeconds = (timeStr) => {
    if (!timeStr) return Infinity;
    const [minutes, seconds] = timeStr.split(":");
    return parseInt(minutes) * 60 + parseFloat(seconds);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = (seconds % 60).toFixed(2);
    return `${mins.toString().padStart(2, "0")}:${secs.padStart(5, "0")}`;
  };

  const renderBasicInfo = () => (
    <div>
      {/* Swimming Event Guidance */}
      <div className={formClasses.guidanceBox}>
        <h4 className={formClasses.guidanceTitle}>
          🏊‍♂️ Swimming Event Guide
        </h4>
        <div className={formClasses.guidanceContent}>
          <p>
            <strong>Event Types:</strong> Individual races or Relay teams (4
            swimmers)
          </p>
          <p>
            <strong>Strokes:</strong> Freestyle, Backstroke, Breaststroke,
            Butterfly, Individual Medley
          </p>
          <p>
            <strong>Distances:</strong> 50m, 100m, 200m, 400m, 800m, 1500m (pool
            dependent)
          </p>
          <p>
            <strong>Time Format:</strong> MM:SS.mm
            (minutes:seconds.milliseconds)
          </p>
          <p>
            <strong>Scoring:</strong> Fastest time wins, places assigned
            automatically
          </p>
          <p>
            <strong>Splits:</strong> Intermediate times recorded for longer
            distances
          </p>
          <p>
            <strong>Tip:</strong> Times auto-validate for reasonableness. Relay
            teams need exactly 4 swimmers!
          </p>
        </div>
      </div>

      <div className={formClasses.formGroup}>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-2">
            <label className={formClasses.label}>Event Name</label>
            <input
              type="text"
              value={localEvent.eventName}
              onChange={(e) =>
                updateParent({ ...localEvent, eventName: e.target.value })
              }
              className={formClasses.input}
              placeholder="e.g., Men's 100m Freestyle"
            />
          </div>
          <div className="flex flex-col gap-2">
            <label className={formClasses.label}>Event Type</label>
            <select
              value={localEvent.eventType}
              onChange={(e) =>
                updateParent({ ...localEvent, eventType: e.target.value })
              }
              className={`${formClasses.select} w-full`}
            >
              <option value="individual">Individual</option>
              <option value="relay">Relay (4 swimmers)</option>
            </select>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2 mt-3">
          <div className="flex flex-col gap-2">
            <label className={formClasses.label}>Stroke</label>
            <select
              value={localEvent.stroke}
              onChange={(e) =>
                updateParent({ ...localEvent, stroke: e.target.value })
              }
              className={`${formClasses.select} w-full`}
            >
              <option value="freestyle">Freestyle</option>
              <option value="backstroke">Backstroke</option>
              <option value="breaststroke">Breaststroke</option>
              <option value="butterfly">Butterfly</option>
              <option value="individual-medley">Individual Medley</option>
            </select>
          </div>
          <div className="flex flex-col gap-2">
            <label className={formClasses.label}>Distance (meters)</label>
            <div className="flex gap-2">
              <select
                value={localEvent.distance}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "custom") {
                    updateParent({
                      ...localEvent,
                      distance: "",
                      customDistance: true,
                    });
                  } else {
                    updateParent({
                      ...localEvent,
                      distance: Number(value),
                      customDistance: false,
                    });
                  }
                }}
                className={`${formClasses.select} flex-1`}
              >
                <option value={50}>50m</option>
                <option value={100}>100m</option>
                <option value={200}>200m</option>
                <option value={400}>400m</option>
                <option value={800}>800m</option>
                <option value={1500}>1500m</option>
                <option value="custom">Custom Distance</option>
              </select>
              {localEvent.customDistance && (
                <input
                  type="number"
                  value={localEvent.distance}
                  onChange={(e) =>
                    updateParent({
                      ...localEvent,
                      distance: Number(e.target.value) || 0,
                    })
                  }
                  placeholder="Enter distance"
                  className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  min="1"
                  max="10000"
                />
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2 mt-3">
          <label className={formClasses.label}>Pool Length</label>
          <div className="flex gap-2 max-w-md">
            <select
              value={localEvent.poolLength}
              onChange={(e) => {
                const value = e.target.value;
                if (value === "custom") {
                  updateParent({
                    ...localEvent,
                    poolLength: "",
                    customPoolLength: true,
                  });
                } else {
                  updateParent({
                    ...localEvent,
                    poolLength: Number(value),
                    customPoolLength: false,
                  });
                }
              }}
              className={`${formClasses.select} flex-1`}
            >
              <option value={25}>25m Pool</option>
              <option value={50}>50m Pool</option>
              <option value="custom">Custom Pool Length</option>
            </select>
            {localEvent.customPoolLength && (
              <input
                type="number"
                value={localEvent.poolLength}
                onChange={(e) =>
                  updateParent({
                    ...localEvent,
                    poolLength: Number(e.target.value) || 0,
                  })
                }
                placeholder="Enter pool length"
                className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                min="10"
                max="100"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderSwimmers = () => (
    <div>
      {localEvent.eventType === "individual" ? (
        <div className={formClasses.teamSection}>
          <h4 className="text-lg font-semibold text-gray-800 mb-2">
            Individual Swimmers ({localEvent.participants.length} entered)
          </h4>
          <SwimmerSelector
            availableUsers={availableUsers}
            onAddSwimmer={(userInfo, customName) =>
              addParticipant(
                userInfo,
                customName,
                localEvent.participants.length + 1
              )
            }
            excludedSwimmerIds={localEvent.participants
              .filter((p) => p.userId)
              .map((p) => p.userId)}
          />
          <div className="mt-4 space-y-3">
            {localEvent.participants.map((participant, index) => (
              <div key={participant.id} className={formClasses.playerCard}>
                <div className="flex items-center space-x-4">
                  <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm font-medium">
                    Lane {participant.lane}
                  </span>
                  <span className="font-medium text-gray-800">
                    {participant.name} {participant.isCustom && "(Custom)"}
                  </span>
                  {participant.place > 0 && (
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        participant.place === 1
                          ? "bg-yellow-100 text-yellow-800"
                          : participant.place === 2
                          ? "bg-gray-100 text-gray-800"
                          : participant.place === 3
                          ? "bg-orange-100 text-orange-800"
                          : "bg-blue-100 text-blue-800"
                      }`}
                    >
                      {participant.place === 1
                        ? "🥇 1st"
                        : participant.place === 2
                        ? "🥈 2nd"
                        : participant.place === 3
                        ? "🥉 3rd"
                        : `${participant.place}th`}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => removeParticipant(index)}
                  className={formClasses.deleteButton}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className={formClasses.teamSection}>
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-lg font-semibold text-gray-800">
              Relay Teams ({localEvent.relayTeams.length} teams)
            </h4>
            <button
              onClick={() =>
                addRelayTeam(`Team ${localEvent.relayTeams.length + 1}`)
              }
              className={formClasses.addButton}
            >
              Add Relay Team
            </button>
          </div>

          <div className="space-y-6">
            {localEvent.relayTeams.map((team, teamIndex) => (
              <div key={team.id} className="bg-white rounded-lg p-4 border border-gray-200">
                <div className="flex justify-between items-center mb-4">
                  <div className="flex items-center space-x-4">
                    <input
                      type="text"
                      value={team.teamName}
                      onChange={(e) => {
                        const updated = { ...localEvent };
                        updated.relayTeams[teamIndex].teamName = e.target.value;
                        updateParent(updated);
                      }}
                      className="px-3 py-2 border border-gray-300 rounded-lg font-medium"
                      placeholder="Team name"
                    />
                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm font-medium">
                      Lane {team.lane}
                    </span>
                    {team.place > 0 && (
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          team.place === 1
                            ? "bg-yellow-100 text-yellow-800"
                            : team.place === 2
                            ? "bg-gray-100 text-gray-800"
                            : team.place === 3
                            ? "bg-orange-100 text-orange-800"
                            : "bg-blue-100 text-blue-800"
                        }`}
                      >
                        {team.place === 1
                          ? "🥇 1st"
                          : team.place === 2
                          ? "🥈 2nd"
                          : team.place === 3
                          ? "🥉 3rd"
                          : `${team.place}th`}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => removeRelayTeam(teamIndex)}
                    className={formClasses.deleteButton}
                  >
                    Remove Team
                  </button>
                </div>

                <div className="mb-4">
                  <SwimmerSelector
                    availableUsers={availableUsers}
                    onAddSwimmer={(userInfo, customName) =>
                      addSwimmerToRelay(teamIndex, userInfo, customName)
                    }
                    isRelay={true}
                    currentCount={team.swimmers.length}
                    excludedSwimmerIds={[
                      ...localEvent.participants
                        .filter((p) => p.userId)
                        .map((p) => p.userId),
                      ...localEvent.relayTeams.flatMap((t) =>
                        t.swimmers.filter((s) => s.userId).map((s) => s.userId)
                      ),
                    ]}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  {[1, 2, 3, 4].map((leg) => {
                    const swimmer = team.swimmers.find((s) => s.leg === leg);
                    return (
                      <div key={leg} className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-sm font-medium text-gray-600 mb-1">
                          Leg {leg}
                        </p>
                        <p className="font-medium">
                          {swimmer ? swimmer.name : "Empty"}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderResults = () => (
    <div>
      {/* Results Guide */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-5 shadow-sm mb-5">
        <h4 className="text-lg font-semibold text-yellow-800 mb-3 flex items-center gap-2">
          📊 Results Guide
        </h4>
        <div className="text-yellow-700 space-y-2 text-sm leading-relaxed">
          <p>
            <strong>Final Time:</strong> Total race time (format: MM:SS.mm,
            e.g., 1:23.45)
          </p>
          <p>
            <strong>Reaction Time:</strong> Start reaction time in seconds
            (e.g., 0.65s - faster is better, under 0.1s is false start)
          </p>
          <p>
            <strong>Stroke Count:</strong> Total number of strokes taken during
            the race (efficiency metric - fewer strokes usually better)
          </p>
          <p>
            <strong>Places:</strong> Auto-calculated based on final times
            (fastest wins)
          </p>
          <p>
            <strong>Tip:</strong> Professional swimmers track these metrics to
            analyze technique and performance!
          </p>
        </div>
      </div>

      {localEvent.eventType === "individual" ? (
        <div className={formClasses.teamSection}>
          <h4 className="text-lg font-semibold text-gray-800 mb-4">
            Individual Results
          </h4>
          <div className={formClasses.tableWrapper}>
            <table className={formClasses.table}>
              <thead>
                <tr>
                  <th className={formClasses.tableHeader}>Place</th>
                  <th className={formClasses.tableHeader}>Lane</th>
                  <th className={formClasses.tableHeader}>Swimmer</th>
                  <th className={`${formClasses.tableHeader} text-center`}>Final Time</th>
                  <th className={`${formClasses.tableHeader} text-center`}>Reaction (s)</th>
                  <th className={`${formClasses.tableHeader} text-center`}>Strokes</th>
                </tr>
              </thead>
              <tbody>
                {localEvent.participants.map((participant, index) => (
                  <tr key={participant.id}>
                    <td className={formClasses.tableCell}>
                      {participant.place > 0 ? (
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            participant.place === 1
                              ? "bg-yellow-100 text-yellow-800"
                              : participant.place === 2
                              ? "bg-gray-100 text-gray-800"
                              : participant.place === 3
                              ? "bg-orange-100 text-orange-800"
                              : "bg-blue-100 text-blue-800"
                          }`}
                        >
                          {participant.place}
                        </span>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className={formClasses.tableCell}>{participant.lane}</td>
                    <td className={`${formClasses.tableCell} font-medium`}>{participant.name}</td>
                    <td className={`${formClasses.tableCell} text-center`}>
                      <input
                        type="text"
                        value={participant.finalTime}
                        onChange={(e) =>
                          updateParticipant(index, "finalTime", e.target.value)
                        }
                        placeholder="MM:SS.mm"
                        className="w-24 px-2 py-1 border rounded text-center"
                      />
                    </td>
                    <td className={`${formClasses.tableCell} text-center`}>
                      <input
                        type="text"
                        value={participant.reaction}
                        onChange={(e) =>
                          updateParticipant(index, "reaction", e.target.value)
                        }
                        placeholder="0.65"
                        className="w-16 px-2 py-1 border rounded text-center"
                      />
                    </td>
                    <td className={`${formClasses.tableCell} text-center`}>
                      <input
                        type="number"
                        value={participant.strokeCount}
                        onChange={(e) =>
                          updateParticipant(
                            index,
                            "strokeCount",
                            Number(e.target.value)
                          )
                        }
                        className="w-16 px-2 py-1 border rounded text-center"
                        min="0"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className={formClasses.teamSection}>
          <h4 className="text-lg font-semibold text-gray-800 mb-4">
            Relay Results
          </h4>
          <div className="space-y-6">
            {localEvent.relayTeams.map((team, teamIndex) => (
              <div key={team.id} className="border rounded-lg p-4 border-gray-200">
                <div className="flex justify-between items-center mb-4">
                  <h5 className="font-semibold text-gray-800">
                    {team.teamName}
                  </h5>
                  <div className="flex items-center space-x-4">
                    <span className={formClasses.label}>Total Time:</span>
                    <input
                      type="text"
                      value={team.totalTime}
                      onChange={(e) => {
                        const updated = { ...localEvent };
                        updated.relayTeams[teamIndex].totalTime =
                          e.target.value;
                        calculatePlaces(updated);
                        updateParent(updated);
                      }}
                      placeholder="MM:SS.mm"
                      className="w-24 px-2 py-1 border rounded text-center"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {[1, 2, 3, 4].map((leg) => {
                    const swimmer = team.swimmers.find((s) => s.leg === leg);
                    const split = team.splits.find((s) => s.leg === leg) || {
                      leg,
                      time: "",
                    };

                    return (
                      <div key={leg} className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-sm font-medium text-gray-600 mb-2">
                          Leg {leg}
                        </p>
                        <p className="font-medium mb-2">
                          {swimmer ? swimmer.name : "Empty"}
                        </p>
                        <input
                          type="text"
                          value={split.time}
                          onChange={(e) => {
                            const updated = { ...localEvent };
                            let splitIndex = updated.relayTeams[
                              teamIndex
                            ].splits.findIndex((s) => s.leg === leg);
                            if (splitIndex === -1) {
                              updated.relayTeams[teamIndex].splits.push({
                                leg,
                                time: e.target.value,
                              });
                            } else {
                              updated.relayTeams[teamIndex].splits[
                                splitIndex
                              ].time = e.target.value;
                            }
                            updateParent(updated);
                          }}
                          placeholder="MM:SS.mm"
                          className="w-full px-2 py-1 border rounded text-center text-sm"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Event Summary */}
      <div className="flex justify-between bg-white p-5 rounded-lg mt-5 shadow-sm flex-col md:flex-row gap-3">
        <div className="text-center p-4 rounded-lg min-w-[120px] bg-gray-50 w-full md:w-auto">
          <p className="text-sm text-gray-500 mb-1">Event</p>
          <p className="text-3xl font-bold text-gray-800">
            {localEvent.distance}m {localEvent.stroke.replace("-", " ")}
          </p>
        </div>
        <div className="text-center p-4 rounded-lg min-w-[120px] bg-gray-50 w-full md:w-auto">
          <p className="text-sm text-gray-500 mb-1">Participants</p>
          <p className="text-3xl font-bold text-gray-800">
            {localEvent.eventType === "individual"
              ? localEvent.participants.length
              : `${localEvent.relayTeams.length} teams`}
          </p>
        </div>
        <div className="text-center p-4 rounded-lg bg-blue-50 min-w-[120px]">
          <p className="text-sm text-gray-500 mb-1">Winning Time</p>
          <p className="text-base font-semibold text-blue-800">
            {localEvent.eventType === "individual"
              ? localEvent.participants.find((p) => p.place === 1)
                  ?.finalTime || "N/A"
              : localEvent.relayTeams.find((t) => t.place === 1)?.totalTime ||
                "N/A"}
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div className={formClasses.matchCard}>
      <div className={formClasses.matchHeader}>
        <h3 className={formClasses.matchTitle}>
          Swimming Event #{eventNumber}
        </h3>
        <button onClick={onRemove} className={formClasses.removeButton}>
          Remove Event
        </button>
      </div>

      <div className={formClasses.tabContainer}>
        {[
          { key: "basic", label: "Event Info" },
          { key: "swimmers", label: "Swimmers/Teams" },
          { key: "results", label: "Results" },
        ].map((tab) => (
          <button
            key={tab.key}
            className={formClasses.tab}
            style={{
              ...tabBaseStyle,
              ...(activeTab === tab.key ? tabActiveStyle : {}),
            }}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "basic" && renderBasicInfo()}
      {activeTab === "swimmers" && renderSwimmers()}
      {activeTab === "results" && renderResults()}
    </div>
  );
};

const SwimmerSelector = ({
  availableUsers,
  onAddSwimmer,
  isRelay = false,
  currentCount = 0,
  excludedSwimmerIds = [],
}) => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [customName, setCustomName] = useState("");
  const [useCustomName, setUseCustomName] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Fetch user suggestions from API
  const fetchUserSuggestions = async (term) => {
    if (!term || term.trim().length < 2) {
      setSuggestions([]);
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/common/search-users?term=${term}`
      );
      if (response.ok) {
        const data = await response.json();
        setSuggestions(data);
      }
    } catch (error) {
      console.error("Error fetching user suggestions:", error);
      setSuggestions([]);
    }
  };

  // Debounced search
  useEffect(() => {
    if (!useCustomName && searchTerm) {
      const timer = setTimeout(() => {
        fetchUserSuggestions(searchTerm);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setSuggestions([]);
    }
  }, [searchTerm, useCustomName]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setShowSuggestions(true);
    setSelectedUser(null);
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setSearchTerm(user.full_name || user.username);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const handleAdd = () => {
    if (isRelay && currentCount >= 4) {
      toast.error("Relay teams can only have 4 swimmers");
      return;
    }

    if (useCustomName && customName.trim()) {
      onAddSwimmer(null, customName.trim());
      setCustomName("");
    } else if (!useCustomName && selectedUser) {
      onAddSwimmer(selectedUser, "");
      setSelectedUser(null);
      setSearchTerm("");
    }
  };

  const ps = {
    container: "flex flex-wrap gap-3 items-center relative",
    searchContainer: "relative flex-1 min-w-[250px]",
    input:
      "w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm",
    suggestionsDropdown:
      "absolute top-full left-0 right-0 bg-white border border-gray-300 border-t-0 rounded-b-lg max-h-[200px] overflow-y-auto z-[1000] shadow-lg",
    suggestionItem:
      "px-3 py-2 border-b border-gray-100 transition-colors text-sm",
    button:
      "bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 text-sm",
    toggle: "text-blue-600 hover:text-blue-800 text-sm underline",
    noResults: "px-3 py-2 text-sm text-gray-600 text-center",
  };

  return (
    <div className={ps.container}>
      {!useCustomName ? (
        <>
          <div className={ps.searchContainer}>
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => {
                // Delay to allow click on suggestion
                setTimeout(() => setShowSuggestions(false), 200);
              }}
              placeholder="Search for system users..."
              className={ps.input}
            />
            {showSuggestions && suggestions.length > 0 && (
              <div className={ps.suggestionsDropdown}>
                {suggestions.map((user) => {
                  const isAlreadySelected =
                    excludedSwimmerIds.includes(user.id) ||
                    excludedSwimmerIds.includes(user.user_id);
                  return (
                    <div
                      key={user.id || user.user_id}
                      className={`${ps.suggestionItem} ${
                        isAlreadySelected
                          ? "opacity-50 cursor-not-allowed bg-gray-100"
                          : "cursor-pointer hover:bg-gray-50"
                      }`}
                      onClick={() =>
                        !isAlreadySelected && handleSelectUser(user)
                      }
                    >
                      <div className="font-medium">
                        {user.full_name || user.username}
                        {isAlreadySelected && (
                          <span className="text-red-500 ml-2">
                            (Already Added)
                          </span>
                        )}
                      </div>
                      {user.email && (
                        <div className="text-xs text-gray-600">
                          {user.email}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {showSuggestions &&
              searchTerm.length >= 2 &&
              suggestions.length === 0 && (
                <div className={ps.suggestionsDropdown}>
                  <div className={ps.noResults}>No users found</div>
                </div>
              )}
          </div>
          <button
            onClick={handleAdd}
            disabled={!selectedUser}
            className={`${ps.button} ${
              selectedUser ? "" : "opacity-50 cursor-not-allowed"
            }`}
          >
            Add {isRelay ? "to Team" : "Swimmer"}
          </button>
        </>
      ) : (
        <>
          <input
            type="text"
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            placeholder="Enter custom swimmer name"
            className={ps.input}
          />
          <button
            onClick={handleAdd}
            disabled={!customName.trim()}
            className={`${ps.button} ${
              customName.trim() ? "" : "opacity-50 cursor-not-allowed"
            }`}
          >
            Add {isRelay ? "to Team" : "Swimmer"}
          </button>
        </>
      )}

      <span
        onClick={() => setUseCustomName(!useCustomName)}
        className={ps.toggle}
      >
        {useCustomName ? "Use system users" : "Add custom name"}
      </span>

      {isRelay && (
        <span className="text-sm text-gray-600">
          ({currentCount}/4 swimmers)
        </span>
      )}
    </div>
  );
};

export default AddSwimming;
